import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FirmStorySection = () => {
  const milestones = [
    {
      year: "2008",
      title: "MBA Completed",
      description: "Mahmood earns his MBA, laying the foundation for advisory and valuation work.",
      icon: "Rocket"
    },
    {
      year: "2009-2021",
      title: "Professional Growth",
      description: " Years of consulting, financial analysis, and hands-on advisory experience supporting businesses, investors, and founders across varied industries.",
      icon: "Award"
    },
    {
      year: "2011",
      title: "CPA Expertise",
      description: "Thomas earns his CPA credential and builds experience in accounting, financial reporting, and advisory services.",
      icon: "Scale"
    },
    {
      year: "2022",
      title: "Independent Practices Established",
      description: "Mahmood establishes Saturn Consultants and Thomas establishes his CPA practice focused on accounting, tax, and advisory support.",
      icon: "Globe"
    },
    {
      year: "2024",
      title: "CVA Certifications",
      description: "Both founding partners earn the Certified Valuation Analyst (CVA) credential, strengthening formal valuation capabilities.",
      icon: "Cpu"
    },
    {
      year: "2024-2025",
      title: "Litigation-Related Valuation Experience",
      description: "Mahmood completes valuation assignments used in legal and settlement contexts, contributing to growing expertise in dispute-related work.",
      icon: "TrendingUp"
    },
    {
      year: "2025",
      title: "FMVA Credential & Northbridge Formation",
      description: " Mahmood earns the FMVA certification and Northbridge Valuation & Advisory is formed to consolidate valuation expertise into a dedicated, independent practice.",
      icon: "Globe"
    },
    // {
    //   year: "2024",
    //   title: "CVA Certifications",
    //   description: "Both founding partners earn the Certified Valuation Analyst (CVA) credential, strengthening formal valuation capabilities.",
    //   icon: "Cpu"
    // },
  ];

  const values = [
    {
      title: "Independence",
      description: "Maintaining objectivity and professional independence in all engagements",
      icon: "Shield"
    },
    {
      title: "Precision",
      description: "Delivering accurate, defensible valuations based on rigorous methodology",
      icon: "Target"
    },
    {
      title: "Transparency",
      description: "Clear communication and transparent processes throughout every engagement",
      icon: "Eye"
    },
    {
      title: "Professional Integrity",
      description: "Commitment to ethical practice, confidentiality, and trust",
      icon: "MapPin"
    }
  ];

  return (
    <div className="bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {/* Firm Story Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold text-text-primary mb-4">
            Our Story & Mission
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Northbridge Valuation & Advisory was established to deliver clear, defensible valuation work grounded in professional standards, disciplined analysis, and transparent methodology. Our goal is to provide clients and their legal and financial advisors with objective valuations that stand up to scrutiny.          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-card rounded-xl shadow-brand border border-border p-8 mb-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-6">
                <Icon name="Compass" size={32} className="text-accent" />
              </div>
              <h3 className="text-2xl font-semibold text-text-primary mb-4">
                Our Mission
              </h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                To deliver independent, well-supported valuation opinions that help clients make informed decisions, resolve disputes, and navigate complex financial matters with confidence.              </p>
              {/* <div className="flex items-center text-sm text-accent font-medium">
                <Icon name="ArrowRight" size={16} className="mr-2" />
                Trusted by 200+ clients across 6 GCC countries
              </div> */}
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <Image
                  src="/img3.png"
                  alt="Modern office environment representing our professional approach"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-accent text-white p-4 rounded-lg shadow-brand">
                <div className="text-2xl font-bold">50+</div>
                <div className="text-sm opacity-90">Valuations Completed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-text-primary text-center mb-12">
            Our Journey
          </h3>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-border hidden lg:block"></div>

            <div className="space-y-8 lg:space-y-12">
              {milestones?.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:pr-8 lg:text-right' : 'lg:pl-8'}`}>
                    <div className="bg-card rounded-lg shadow-brand border border-border p-6">
                      <div className="flex items-center mb-3 lg:justify-end">
                        <div className={`flex items-center ${index % 2 === 0 ? 'lg:flex-row-reverse' : ''}`}>
                          <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center mr-3 lg:mr-0 lg:ml-3">
                            <Icon name={milestone?.icon} size={20} className="text-accent" />
                          </div>
                          <span className="text-2xl font-bold text-accent">{milestone?.year}</span>
                        </div>
                      </div>
                      <h4 className="text-lg font-semibold text-text-primary mb-2">
                        {milestone?.title}
                      </h4>
                      <p className="text-text-secondary">
                        {milestone?.description}
                      </p>
                    </div>
                  </div>

                  {/* Timeline dot */}
                  <div className="hidden lg:flex w-4 h-4 bg-accent rounded-full border-4 border-background z-10"></div>

                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div>
          <h3 className="text-2xl font-semibold text-text-primary text-center mb-12">
            Our Core Values
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((value, index) => (
              <div key={index} className="bg-card rounded-lg shadow-brand border border-border p-6 text-center hover-lift">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-4">
                  <Icon name={value?.icon} size={24} className="text-accent" />
                </div>
                <h4 className="text-lg font-semibold text-text-primary mb-3">
                  {value?.title}
                </h4>
                <p className="text-sm text-text-secondary">
                  {value?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmStorySection;